import { useCallback, useEffect, useRef, useState } from 'react';
import { pepApiRequest, RequestMethod } from 'src/services';
import { HMIField, HMILiveValues, HMILogEntry, HMIRestApiConfig, HMITemplate } from './types';
import { evaluateEnabledWhen, validateRange } from './expression';
import { validateFieldRules } from './rules';
import { applyComputedFormulas } from './computedFields';

/**
 * In-browser simulation of the BMT Edge Compute <-> PEPSense Universal Edge
 * <-> PEPSense Cloud Platform round trip shown in the architecture diagram:
 *
 *   1. GET  request polls BMT for current tag values (every `pollIntervalSec`)
 *   2. BMT responds with the Test_Info.ini-style payload
 *   3. POST request pushes an operator recipe/command change down to BMT
 *   4. BMT acknowledges (or rejects) the write
 *
 * For the POC this whole cycle happens client-side with randomised values and
 * artificial latency instead of a real HTTP call, so the UI/UX and validation
 * rules can be demoed without any backend. Swap `pollOnce`/`sendCommand` for
 * real `pepApiRequest` calls to go to production (see docs).
 *
 * The one exception is `kind: "action"` buttons configured with
 * `actionBehavior: "rest_api"` (see `runAction`/`callRestApiAction` below):
 * those already perform a real HTTP call using the field's `restApiConfig`,
 * so an HMI page can trigger a live REST API from a button click today
 * without waiting on the rest of this simulator being replaced.
 */

const REST_METHOD_TO_REQUEST_METHOD: Record<HMIRestApiConfig['method'], RequestMethod> = {
  GET: RequestMethod.Get,
  POST: RequestMethod.Post,
  PUT: RequestMethod.Put,
  PATCH: RequestMethod.Patch,
  DELETE: RequestMethod.Delete,
};

/** Replaces every `{{fieldId}}` placeholder with that field's current live value. */
function interpolate(templateStr: string, values: HMILiveValues): string {
  return templateStr.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_match, id: string) => {
    const v = values[id];
    return v === undefined || v === null ? '' : String(v);
  });
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Reads a dotted/indexed path (e.g. `data.items.0.status`) out of a parsed JSON value. */
function getByPath(source: unknown, path: string): unknown {
  return path
    .split('.')
    .filter(Boolean)
    .reduce<unknown>((acc, key) => {
      if (acc === null || acc === undefined) return undefined;
      return (acc as Record<string, unknown>)[key];
    }, source);
}

/**
 * Calls the REST endpoint configured on a `kind: "action"` button
 * (`actionBehavior: "rest_api"`), substituting `{{fieldId}}` placeholders in
 * the URL/headers/body with current field values. A `url` starting with `/`
 * goes through `pepApiRequest` (proxied + authenticated); any other URL is
 * called directly with `fetch` so the button can target an external system
 * (e.g. the BMT edge device) without PEP's own auth headers.
 */
async function callRestApiAction(
  config: HMIRestApiConfig,
  values: HMILiveValues,
  appendLog: (level: HMILogEntry['level'], message: string) => void,
  caption: string
): Promise<{ ok: boolean; error?: string; responseValues?: Partial<HMILiveValues> }> {
  const url = interpolate(config.url, values);
  const headers: Record<string, string> = {};
  (config.headers ?? []).forEach(({ key, value }) => {
    if (key) headers[key] = interpolate(value, values);
  });

  const sendsBody = config.method !== 'GET' && config.method !== 'DELETE';
  const interpolatedBody = sendsBody && config.body ? interpolate(config.body, values) : undefined;
  let parsedBody: Record<string, unknown> | undefined;
  if (interpolatedBody) {
    try {
      parsedBody = JSON.parse(interpolatedBody);
    } catch {
      appendLog('WARN', `${caption}: REST API body is not valid JSON - sent as raw text instead.`);
    }
  }

  appendLog('INFO', `${config.method} ${url}${interpolatedBody ? ` ${interpolatedBody}` : ''}`);

  try {
    let responseBody: unknown;
    if (url.startsWith('/')) {
      responseBody = await pepApiRequest({
        method: REST_METHOD_TO_REQUEST_METHOD[config.method],
        url,
        headers,
        params: (parsedBody ?? null) as never,
      });
    } else {
      const res = await fetch(url, {
        method: config.method,
        headers: { 'Content-Type': 'application/json', ...headers },
        body: parsedBody ? JSON.stringify(parsedBody) : interpolatedBody,
      });
      const text = await res.text();
      responseBody = text ? safeJsonParse(text) : undefined;
      if (!res.ok) {
        throw new Error(`HTTP ${res.status} ${res.statusText}`);
      }
    }

    const responseValues: Partial<HMILiveValues> = {};
    (config.responseMappings ?? []).forEach(({ fieldId, responsePath }) => {
      const value = getByPath(responseBody, responsePath);
      if (value !== undefined) responseValues[fieldId] = value as never;
    });

    appendLog('INFO', `${caption} completed (REST API call succeeded).`);
    return { ok: true, responseValues };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    appendLog('ERROR', `${caption} failed: ${message}`);
    return { ok: false, error: message };
  }
}

// Baseline anchors lifted from a real BMT Test_Info.ini capture so the demo
// values look plausible instead of pure random noise.
const LIVE_BASELINES: Record<string, { base: number; jitter: number }> = {
  supply_pressure: { base: 29.5, jitter: 1.2 },
  pb_inlet_pressure: { base: 11.9, jitter: 0.6 },
  pb_pressure: { base: 7.5, jitter: 0.5 },
  oil_bath_temperature: { base: 100, jitter: 2 },
  ir_monitor_temperature: { base: 47.4, jitter: 3 },
  storage_free: { base: 15, jitter: 0.5 },
};

const BOOL_INDICATOR_DEFAULTS: Record<string, boolean> = {
  ir_oven_ready: true,
  oil_bath_ready: false,
  pb_pressure_ready: true,
  estop_1: false,
  estop_2: false,
  alert_hdd: false,
  alert_oil_sensor: false,
  alert_init: false,
  alert_drawer: false,
};

const PROGRESS_SEQUENCE = [
  'Idle',
  'Heating',
  'Blowing',
  'THERMOscan',
  'Complete',
];

function initialValueFor(field: HMIField): string | number | boolean | number[] {
  if (field.datatype === 'array') {
    const length = field.arrayLength ?? 0;
    const base = typeof field.default === 'number' ? field.default : 0;
    return Array.from({ length }, () => base);
  }
  if (field.datatype === 'bool') {
    if (field.id in BOOL_INDICATOR_DEFAULTS) return BOOL_INDICATOR_DEFAULTS[field.id];
    return typeof field.default === 'boolean' ? field.default : false;
  }
  if (field.id in LIVE_BASELINES) return LIVE_BASELINES[field.id].base;
  if (field.id === 'progress_stage') return PROGRESS_SEQUENCE[0];
  if (field.id === 'sub_assembly') return 'Mould Blow';
  if (field.id === 'machine_state') return 'Running';
  if (field.id === 'drawer' || field.id === 'door_1' || field.id === 'door_2') return 'Closed';
  if (field.id === 'door_3') return 'Locked';
  if (field.datatype === 'string' || field.datatype === 'enum') {
    if (field.default !== undefined) return field.default as string;
    return (field.options?.[0]?.value as string) ?? '';
  }
  if (typeof field.default === 'number') return field.default;
  return field.rangeMin ?? 0;
}

export function buildInitialValues(fields: HMIField[]): HMILiveValues {
  const values: HMILiveValues = {};
  fields.forEach((field) => {
    values[field.id] = initialValueFor(field);
  });
  return values;
}

function nextLiveTick(field: HMIField, current: HMILiveValues, tick: number): unknown {
  if (field.sourceKind !== 'live') return current[field.id];

  if (field.id in LIVE_BASELINES) {
    const { base, jitter } = LIVE_BASELINES[field.id];
    const value = base + (Math.random() - 0.5) * jitter * 2;
    return Math.round(value * 100) / 100;
  }
  if (field.id === 'progress_stage') {
    return PROGRESS_SEQUENCE[tick % PROGRESS_SEQUENCE.length];
  }
  if (field.datatype === 'bool' && field.id in BOOL_INDICATOR_DEFAULTS) {
    const flipChance = field.id.startsWith('estop') ? 0.01 : 0.08;
    if (Math.random() < flipChance) return !current[field.id];
    return current[field.id];
  }
  if (field.id === 'alert_warning') {
    return Math.random() < 0.05 ? 1 : 0;
  }
  // Generic numeric simulation for fields without a hand-authored baseline
  // above (e.g. new chart/indicator fields added via the layout builder),
  // so a freshly-added trend widget still shows movement in the demo.
  if (['i32', 'f32', 'f64'].includes(field.datatype)) {
    const prev = current[field.id];
    if (typeof prev === 'number') {
      const span = (field.rangeMax ?? prev + 10) - (field.rangeMin ?? prev - 10);
      const jitter = span > 0 ? span * 0.03 : 1;
      const next = prev + (Math.random() - 0.5) * jitter * 2;
      const clamped = Math.min(
        field.rangeMax ?? next,
        Math.max(field.rangeMin ?? next, next)
      );
      return Math.round(clamped * 100) / 100;
    }
  }
  return current[field.id];
}

export interface CommandResult {
  ok: boolean;
  error?: string;
}

export interface UseHmiLiveChannelResult {
  values: HMILiveValues;
  /** Fields currently disabled by an `enabled_when` business rule. */
  disabledFieldIds: Set<string>;
  logs: HMILogEntry[];
  isPolling: boolean;
  lastPolledAt?: string;
  setFieldValue: (field: HMIField, value: unknown) => Promise<CommandResult>;
  runAction: (field: HMIField) => Promise<CommandResult>;
  /**
   * Bulk-applies a partial value map directly (bypassing per-field
   * validation/rules), e.g. filling every Process screen parameter at once
   * from a loaded process file. Logs one summary entry.
   */
  applyValues: (next: Partial<HMILiveValues>, message?: string) => void;
  /** Resets the given fields back to their schema default, e.g. "Clear Process". */
  resetFields: (fields: HMIField[], message?: string) => void;
}

const NETWORK_LATENCY_MS = { min: 150, max: 400 };
function simulateLatency(): Promise<void> {
  const delay =
    NETWORK_LATENCY_MS.min +
    Math.random() * (NETWORK_LATENCY_MS.max - NETWORK_LATENCY_MS.min);
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
}

export function useHmiLiveChannel(
  template: HMITemplate | undefined,
  twinId?: string
): UseHmiLiveChannelResult {
  const [values, setValues] = useState<HMILiveValues>({});
  const [logs, setLogs] = useState<HMILogEntry[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [lastPolledAt, setLastPolledAt] = useState<string | undefined>();
  const tickRef = useRef(0);

  const appendLog = useCallback((level: HMILogEntry['level'], message: string) => {
    setLogs((prev) =>
      [...prev, { timestamp: new Date().toISOString(), level, message }].slice(-200)
    );
  }, []);

  useEffect(() => {
    if (!template) return undefined;
    setValues(applyComputedFormulas(template.computedFormulas, template.fields, buildInitialValues(template.fields)));
    setLogs([
      {
        timestamp: new Date().toISOString(),
        level: 'INFO',
        message: `Connected to twin ${twinId ?? '(unknown)'} using template "${template.name}" v${template.version}. Poll interval ${template.pollIntervalSec}s.`,
      },
    ]);
    tickRef.current = 0;

    const poll = () => {
      setIsPolling(true);
      // Simulate: 1) GET request to BMT Edge, 2) response processed by Data
      // Processor on the Universal Edge, values merged into local state.
      setValues((prev) => {
        const next = { ...prev };
        template.fields.forEach((field) => {
          next[field.id] = nextLiveTick(field, prev, tickRef.current) as never;
        });
        return applyComputedFormulas(template.computedFormulas, template.fields, next);
      });
      tickRef.current += 1;
      setLastPolledAt(new Date().toISOString());
      setIsPolling(false);
    };

    poll();
    const interval = setInterval(poll, template.pollIntervalSec * 1000);
    return () => clearInterval(interval);
  }, [template, twinId]);

  const disabledFieldIds = new Set<string>();
  if (template) {
    template.fields.forEach((field) => {
      if (field.enabledWhen && !evaluateEnabledWhen(field.enabledWhen, values)) {
        disabledFieldIds.add(field.id);
      }
    });
  }

  const setFieldValue = useCallback(
    async (field: HMIField, value: unknown): Promise<CommandResult> => {
      if (field.access === 'read' || field.access === 'derived') {
        return { ok: false, error: 'Field is read-only.' };
      }
      if (disabledFieldIds.has(field.id)) {
        return {
          ok: false,
          error: `Disabled by rule: ${field.enabledWhen}`,
        };
      }
      if (typeof value === 'number') {
        const validationError = validateRange(value, field.rangeMin, field.rangeMax);
        if (validationError) {
          appendLog('WARN', `Rejected ${field.caption} = ${value}: ${validationError}`);
          return { ok: false, error: validationError };
        }
      }
      if (template) {
        const candidateValues = { ...values, [field.id]: value as never };
        const ruleError = validateFieldRules(template.rules, template.fields, candidateValues, field.id);
        if (ruleError) {
          appendLog('WARN', `Rejected ${field.caption} = ${value}: ${ruleError}`);
          return { ok: false, error: ruleError };
        }
      }

      appendLog(
        'INFO',
        `POST recipe change -> ${field.recipePath ?? field.id} = ${JSON.stringify(value)}`
      );
      await simulateLatency();
      setValues((prev) => {
        const next = { ...prev, [field.id]: value as never };
        return template ? applyComputedFormulas(template.computedFormulas, template.fields, next) : next;
      });
      appendLog('INFO', `BMT Edge acknowledged ${field.caption}.`);
      return { ok: true };
    },
    [appendLog, disabledFieldIds, template, values]
  );

  const runAction = useCallback(
    async (field: HMIField): Promise<CommandResult> => {
      appendLog('INFO', `Action triggered: ${field.caption} (${field.id})`);

      if (field.actionBehavior === 'rest_api' && field.restApiConfig) {
        const { ok, error, responseValues } = await callRestApiAction(
          field.restApiConfig,
          values,
          appendLog,
          field.caption
        );
        if (ok && responseValues && Object.keys(responseValues).length > 0) {
          setValues((prev) => {
            const next = { ...prev, ...responseValues } as HMILiveValues;
            return template ? applyComputedFormulas(template.computedFormulas, template.fields, next) : next;
          });
        }
        return { ok, error };
      }

      await simulateLatency();
      appendLog('INFO', `${field.caption} completed.`);
      return { ok: true };
    },
    [appendLog, template, values]
  );

  const applyValues = useCallback(
    (next: Partial<HMILiveValues>, message?: string) => {
      setValues((prev) => {
        const merged = { ...prev, ...next } as HMILiveValues;
        return template ? applyComputedFormulas(template.computedFormulas, template.fields, merged) : merged;
      });
      appendLog(
        'INFO',
        message ?? `Applied ${Object.keys(next).length} field value(s).`
      );
    },
    [appendLog, template]
  );

  const resetFields = useCallback(
    (fields: HMIField[], message?: string) => {
      setValues((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          next[field.id] = initialValueFor(field) as never;
        });
        return template ? applyComputedFormulas(template.computedFormulas, template.fields, next) : next;
      });
      appendLog('INFO', message ?? `Cleared ${fields.length} field value(s).`);
    },
    [appendLog, template]
  );

  return {
    values,
    disabledFieldIds,
    logs,
    isPolling,
    lastPolledAt,
    setFieldValue,
    runAction,
    applyValues,
    resetFields,
  };
}
